import {SwiperModule} from 'swiper/types';
import {SwiperProps} from 'swiper/swiper-react';
import {
	Pagination,
	Autoplay,
	Navigation,
	EffectCreative,
	EffectCoverflow,
	FreeMode,
	Mousewheel,
	Keyboard,
	Thumbs,
	Zoom,
	Scrollbar,
} from 'swiper/modules';

import {ICarouselProps, ECarouselDirection, ConfigType, ChildrenType} from '../../ICarousel';

/**
 * Sets up the Swiper config defaults
 *
 * @returns {ConfigType} The default config
 */
export const setupSwiperConfigDefaults = (): ConfigType => {
	return {
		direction: ECarouselDirection.HORIZONTAL,
		slidesPerView: 1,
		spaceBetween: 0,
		breakpoints: {},
		paginationConfig: null,
		navigationConfig: null,
		freeModeConfig: null,
		freeMode: null,
		scrollBarConfig: null,
		thumbs: null,
		setThumbsSwiper: null,
		loop: false,
		loopedSlides: null,
		autoplay: false,
		zoom: null,
		grabCursor: false,
		effect: 'slide',
		rewind: false,
		mousewheel: false,
		keyboard: false,
		enablePaginationNumbers: false,
		plainNumeration: false,
		touchReleaseOnEdges: false,
		allowTouchMove: true,
		initialSlide: 0,
		speed: 300,
		centeredSlides: false,
		slidesOffsetAfter: 0,
		slidesOffsetBefore: 0,
		resistanceRatio: 1,
		duplicateSlides: {
			enabled: false,
			slidesNumber: 0,
		},
		autoHeight: false,
	};
};

/**
 * Sets up the Swiper config
 *
 * @param {ICarouselProps} config The config
 * @param {boolean} disableNavigationForOneSlide Whether to disable navigation for one slide
 * @param {ChildrenType[]} childrenArray The children array
 * @param {ICarouselProps['classNames']} classNames The class names
 *
 * @returns {ConfigType} The config
 */
export const setupSwiperConfig = (
	config: ConfigType,
	disableNavigationForOneSlide = false,
	childrenArray: ChildrenType[] = [],
	classNames: ICarouselProps['classNames'],
): ConfigType => {
	const defaultConfig = setupSwiperConfigDefaults();

	let currentConfig: ConfigType | any = {};
	Object.keys({...defaultConfig, ...config}).map((key) => {
		currentConfig[key] = config[key] !== undefined ? config[key] : defaultConfig[key];
	});

	if (disableNavigationForOneSlide && childrenArray.length < 2) {
		currentConfig = {
			...currentConfig,
			navigationConfig: {enabled: false},
		};
	}

	if (currentConfig.paginationConfig?.enabled) {
		currentConfig.paginationConfig.el = '.pagination-wrapper';
		currentConfig.paginationConfig.bulletClass = classNames.bullet;
		currentConfig.paginationConfig.bulletActiveClass = classNames.bulletActive;
	}

	if (currentConfig.scrollBarConfig) {
		currentConfig.scrollBarConfig.el = '.swiper-scrollbar';
	}

	return currentConfig;
};

/**
 * Sets up the Swiper modules
 *
 * @param {ConfigType} config The config
 * @returns {SwiperModule[]} The modules
 */
const setupSwiperModules = (config: ConfigType): SwiperModule[] => {
	const modules = [Autoplay];

	if (config.paginationConfig?.enabled) {
		modules.push(Pagination);
	}

	if (config.navigationConfig?.enabled) {
		modules.push(Navigation);
	}

	if (config.creativeEffectConfig) {
		modules.push(EffectCreative);
	}

	if (config.coverflowEffectConfig) {
		modules.push(EffectCoverflow);
	}

	if (config.freeModeConfig || config.freeMode) {
		modules.push(FreeMode);
	}

	if (config.mousewheel) {
		modules.push(Mousewheel);
	}

	if (config.keyboard) {
		modules.push(Keyboard);
	}

	if (config.scrollBarConfig) {
		modules.push(Scrollbar);
	}

	if (config.thumbs || config.setThumbsSwiper) {
		modules.push(Thumbs);
	}

	if (config.zoom) {
		modules.push(Zoom);
	}

	return modules;
};

/**
 * Returns mapped swiper config
 *
 * @param {ConfigType} config The config
 * @returns {SwiperProps} The mapped swiper config
 */
export const getMappedSwiperConfig = (config: ConfigType): SwiperProps => {
	const modules = setupSwiperModules(config);

	return {
		modules: modules,
		pagination: config.paginationConfig,
		initialSlide: config.initialSlide,
		slidesPerView: config.slidesPerView,
		spaceBetween: config.spaceBetween,
		breakpoints: config.breakpoints,
		direction: config.direction,
		autoplay: config.autoplay,
		loop: config.loop,
		loopedSlides: config.loopedSlides,
		virtual: config.virtual,
		navigation: config.navigationConfig,
		scrollbar: config.scrollBarConfig,
		grabCursor: config.grabCursor,
		effect: config.effect,
		creativeEffect: config.creativeEffectConfig,
		coverflowEffect: config.coverflowEffectConfig,
		touchReleaseOnEdges: config.touchReleaseOnEdges,
		rewind: config.rewind,
		mousewheel: config.mousewheel,
		keyboard: config.keyboard,
		allowTouchMove: config.allowTouchMove,
		thumbs: config.thumbs,
		speed: config.speed,
		zoom: config.zoom,
		centeredSlides: config.centeredSlides,
		slidesOffsetAfter: config.slidesOffsetAfter,
		slidesOffsetBefore: config.slidesOffsetBefore,
		resistanceRatio: config.resistanceRatio,
		freeMode: config.freeModeConfig || config.freeMode,
		lazyPreloadPrevNext: config.lazyPreloadPrevNext,
		className: config.customClassName,
		autoHeight: config.autoHeight,
	};
};
